import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  UserCheck, 
  Plus, 
  Edit, 
  Trash2, 
  Check,
  X,
  Users,
  BarChart2,
  AlertCircle,
  Activity,
  Target,
  Zap,
  ArrowRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSIGNMENT_TYPES = [
  { value: 'round_robin', label: 'Round Robin', description: 'Distribute leads evenly in rotation', color: 'round-robin' },
  { value: 'load_balanced', label: 'Load Balanced', description: 'Assign to user with least active leads', color: 'load-balanced' },
  { value: 'score_based', label: 'Score Based', description: 'Assign based on lead score', color: 'score-based' },
  { value: 'service_based', label: 'Service Based', description: 'Assign based on service expertise', color: 'service-based' },
  { value: 'source_based', label: 'Source Based', description: 'Assign based on lead source', color: 'source-based' },
];

const CONDITION_OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'in_list', label: 'In List' },
];

const CONDITION_FIELDS = [
  { value: 'source', label: 'Source' },
  { value: 'service_type', label: 'Service Type' },
  { value: 'lead_score', label: 'Lead Score' },
  { value: 'destination_country', label: 'Destination Country' },
  { value: 'stage', label: 'Stage' },
];

const AssignmentRulesManager = () => {
  const { getAuthHeader, admin } = useAuth();
  const [assignmentRules, setAssignmentRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [workloads, setWorkloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    assignment_type: 'round_robin',
    priority: 0,
    is_active: true,
    conditions: [],
    assign_to_users: []
  });

  const [conditionForm, setConditionForm] = useState({
    field: 'source',
    operator: 'equals',
    value: ''
  });

  useEffect(() => {
    fetchAssignmentRules();
    fetchUsers();
    fetchWorkloads();
  }, []);

  const fetchAssignmentRules = async () => {
    try {
      const response = await axios.get(`${API}/admin/assignment-rules`, {
        headers: getAuthHeader()
      });
      setAssignmentRules(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching assignment rules:', error);
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: getAuthHeader()
      });
      // Filter to only show sales and admin users
      setUsers(response.data.filter(u => u.role === 'sales' || u.role === 'admin'));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchWorkloads = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/workload`, {
        headers: getAuthHeader()
      });
      setWorkloads(response.data);
    } catch (error) {
      console.error('Error fetching workloads:', error);
    }
  };

  const handleCreateRule = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.assign_to_users || formData.assign_to_users.length === 0) {
      alert('Please select at least one user for lead assignment');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/assignment-rules`, formData, {
        headers: getAuthHeader()
      });
      setIsCreateDialogOpen(false);
      resetForm();
      fetchAssignmentRules();
      alert('Assignment rule created successfully!');
    } catch (error) {
      console.error('Error creating assignment rule:', error);
      alert('Failed to create assignment rule: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateRule = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.assign_to_users || formData.assign_to_users.length === 0) {
      alert('Please select at least one user for lead assignment');
      return;
    }
    
    try {
      await axios.put(`${API}/admin/assignment-rules/${editingRule.id}`, formData, {
        headers: getAuthHeader()
      });
      setIsEditDialogOpen(false);
      setEditingRule(null);
      resetForm();
      fetchAssignmentRules();
      alert('Assignment rule updated successfully!');
    } catch (error) {
      console.error('Error updating assignment rule:', error);
      alert('Failed to update assignment rule: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!await window.confirm('Are you sure you want to delete this assignment rule?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/assignment-rules/${ruleId}`, {
        headers: getAuthHeader()
      });
      fetchAssignmentRules();
      alert('Assignment rule deleted successfully!');
    } catch (error) {
      console.error('Error deleting assignment rule:', error);
      alert('Failed to delete assignment rule');
    }
  };

  const handleCleanupRules = async () => {
    if (!await window.confirm('⚠️ This will remove all non-sales users from assignment rules.\n\nOnly users with roles: sales, admin, super_admin will be kept.\n\nContinue?')) {
      return;
    }
    
    try {
      console.log('🔄 Starting cleanup...');
      const response = await axios.post(`${API}/admin/assignment-rules/cleanup`, {}, {
        headers: getAuthHeader()
      });
      
      console.log('✅ Cleanup result:', response.data);
      
      if (response.data.success) {
        const report = response.data.report;
        alert(`✅ Cleanup Successful!\n\n${response.data.message}\n\nRules updated: ${report.rules_updated}\nRules already clean: ${report.rules_already_clean}`);
        
        // Refresh the rules list
        fetchAssignmentRules();
        
        // Log detailed report
        console.table(report.details);
      } else {
        alert('❌ Cleanup failed: ' + response.data.message);
      }
    } catch (error) {
      console.error('❌ Cleanup error:', error);
      const errorMsg = error.response?.data?.detail || error.message;
      alert('❌ Cleanup failed: ' + errorMsg);
    }
  };

  const openEditDialog = (rule) => {
    console.log('Opening edit dialog for rule:', rule);
    setEditingRule(rule);
    
    // Set form data with all rule values
    const newFormData = {
      name: rule.name || '',
      description: rule.description || '',
      assignment_type: rule.assignment_type || 'round_robin',
      priority: rule.priority || 0,
      is_active: rule.is_active !== undefined ? rule.is_active : true,
      conditions: Array.isArray(rule.conditions) ? rule.conditions : [],
      assign_to_users: Array.isArray(rule.assign_to_users) ? rule.assign_to_users : []
    };
    
    console.log('Setting form data:', newFormData);
    setFormData(newFormData);
    
    // Reset condition form
    setConditionForm({
      field: 'source',
      operator: 'equals',
      value: ''
    });
    
    // Use setTimeout to ensure state update before opening dialog
    setTimeout(() => {
      setIsEditDialogOpen(true);
      console.log('Edit dialog opened with form data:', newFormData);
    }, 50);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      assignment_type: 'round_robin',
      priority: 0,
      is_active: true,
      conditions: [],
      assign_to_users: []
    });
    setConditionForm({
      field: 'source',
      operator: 'equals',
      value: ''
    });
  };

  const handleAddCondition = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!conditionForm.field || !conditionForm.operator || !conditionForm.value) {
      alert('Please fill all condition fields');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      conditions: [...prev.conditions, { ...conditionForm }]
    }));
    
    setConditionForm({
      field: 'source',
      operator: 'equals',
      value: ''
    });
  };

  const handleRemoveCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const toggleUserAssignment = (username) => {
    setFormData(prev => {
      const currentUsers = prev.assign_to_users || [];
      if (currentUsers.includes(username)) {
        return {
          ...prev,
          assign_to_users: currentUsers.filter(u => u !== username)
        };
      } else {
        return {
          ...prev,
          assign_to_users: [...currentUsers, username]
        };
      }
    });
  };

  const getTypeInfo = (type) => {
    return ASSIGNMENT_TYPES.find(t => t.value === type) || ASSIGNMENT_TYPES[0];
  };

  if (loading) {
    return (
      <div className="assignment-rules-manager">
        <div className="assignment-header">
          <h1><UserCheck size={24} /> Assignment Rules</h1>
        </div>
        <div className="text-center py-8">Loading assignment rules...</div>
      </div>
    );
  }

  // Render a form section (used in both create and edit dialogs)
  const renderFormFields = () => (
    <div className="space-y-4">
      <div>
        <Label>Rule Name *</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., US Visa - Assign to Sarah"
          required
        />
      </div>
      <div>
        <Label>Description</Label>
        <Input
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Brief description of this rule"
        />
      </div>
      <div>
        <Label>Assignment Type *</Label>
        <Select
          value={formData.assignment_type}
          onValueChange={(value) => setFormData({ ...formData, assignment_type: value })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ASSIGNMENT_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label} - {type.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Priority (Higher = Checked First)</Label>
        <Input
          type="number"
          value={formData.priority}
          onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
          min="0"
          max="100"
        />
      </div>

      {/* Conditions Builder */}
      <div className="conditions-builder">
        <div className="flex items-center justify-between mb-3">
          <Label className="text-base">Conditions ({formData.conditions.length})</Label>
        </div>
        
        {formData.conditions.map((cond, index) => (
          <div key={index} className="assignment-condition-item">
            <span className="font-mono text-sm">
              {cond.field} {cond.operator} "{cond.value}"
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleRemoveCondition(index)}
              className="ml-auto text-red-600"
            >
              <X size={16} />
            </Button>
          </div>
        ))}

        <div className="condition-row">
          <div>
            <Label className="text-xs">Field</Label>
            <Select
              value={conditionForm.field}
              onValueChange={(value) => setConditionForm({ ...conditionForm, field: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONDITION_FIELDS.map((field) => (
                  <SelectItem key={field.value} value={field.value}>
                    {field.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Operator</Label>
            <Select
              value={conditionForm.operator}
              onValueChange={(value) => setConditionForm({ ...conditionForm, operator: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONDITION_OPERATORS.map((op) => (
                  <SelectItem key={op.value} value={op.value}>
                    {op.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Value</Label>
            <Input
              value={conditionForm.value}
              onChange={(e) => setConditionForm({ ...conditionForm, value: e.target.value })}
              placeholder="Value"
            />
          </div>
          <div>
            <Label className="text-xs opacity-0">Add</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddCondition}
            >
              <Plus size={16} />
            </Button>
          </div>
        </div>
      </div>

      {/* User Assignment */}
      <div>
        <Label className="text-base mb-2">Assign To Users *</Label>
        <p className="text-xs text-muted-foreground mb-2">
          Select users for this rule. Leads will be distributed among selected users using{' '}
          {formData.assignment_type === 'round_robin' && 'round-robin rotation based on user priority.'}
          {formData.assignment_type === 'load_balanced' && 'load balancing (least active leads first).'}
          {formData.assignment_type === 'score_based' && 'lead score (high-score leads to highest priority user).'}
          {formData.assignment_type === 'service_based' && 'round-robin rotation based on user priority.'}
          {formData.assignment_type === 'source_based' && 'round-robin rotation based on user priority.'}
        </p>
        <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border">
          {users.length === 0 ? (
            <p className="text-sm text-muted-foreground">No users available. Create users in User Management.</p>
          ) : (
            users.map((user) => {
              const isSelected = formData.assign_to_users.includes(user.username);
              return (
                <label
                  key={user.username}
                  className="cursor-pointer"
                  title={`Priority: ${user.priority || 'Not set'}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    border: isSelected ? '2px solid #15803d' : '2px solid #d1d5db',
                    background: isSelected ? '#16a34a' : '#ffffff',
                    color: isSelected ? '#ffffff' : '#374151',
                    boxShadow: isSelected ? '0 2px 4px rgba(22, 163, 74, 0.2)' : '0 1px 2px rgba(0, 0, 0, 0.05)'
                  }}
                  onMouseEnter={(e) => {
                    if (isSelected) {
                      e.currentTarget.style.background = '#15803d';
                    } else {
                      e.currentTarget.style.background = '#f3f4f6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isSelected) {
                      e.currentTarget.style.background = '#16a34a';
                    } else {
                      e.currentTarget.style.background = '#ffffff';
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={isSelected}
                    onChange={() => toggleUserAssignment(user.username)}
                  />
                  <Users size={14} />
                  {user.username}
                  {user.priority && (
                    <span className="ml-1 text-xs opacity-70">(P{user.priority})</span>
                  )}
                </label>
              );
            })
          )}
        </div>
        {formData.assign_to_users.length === 0 && (
          <p className="text-xs text-amber-600 mt-1">⚠️ Please select at least one user</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="assignment-rules-manager">
      <div className="assignment-header">
        <div>
          <h1>
            <UserCheck size={28} /> Lead Assignment Rules
          </h1>
          <p className="text-muted-foreground">Automate lead distribution with intelligent assignment rules</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          {admin?.role === 'super_admin' && (
            <Button 
              variant="outline" 
              onClick={handleCleanupRules}
              style={{ 
                borderColor: '#f59e0b', 
                color: '#f59e0b',
                backgroundColor: '#fffbeb'
              }}
            >
              <AlertCircle size={20} /> Remove Non-Sales Users
            </Button>
          )}
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="scoring-btn-create">
                <Plus size={20} /> Create Assignment Rule
              </Button>
            </DialogTrigger>
          <DialogContent className="assignment-dialog">
            <DialogHeader>
              <DialogTitle>Create New Assignment Rule</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateRule}>
              {renderFormFields()}

              <div className="flex items-center gap-2 mt-4">
                <input
                  type="checkbox"
                  id="is-active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <Label htmlFor="is-active" className="cursor-pointer">
                  <Zap size={16} className="inline" /> Activate Rule Immediately
                </Label>
              </div>

              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create Rule</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Rules List */}
      <div className="assignment-rules-grid">
        {assignmentRules.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <UserCheck size={48} className="mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2">No Assignment Rules Created</h3>
              <p className="text-muted-foreground mb-4">Create your first rule to automate lead distribution</p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus size={20} /> Create First Rule
              </Button>
            </CardContent>
          </Card>
        ) : (
          assignmentRules.map((rule) => {
            const typeInfo = getTypeInfo(rule.assignment_type);
            return (
              <Card key={rule.id} className={`assignment-rule-card ${!rule.is_active ? 'inactive' : ''}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CardTitle className="text-lg">{rule.name}</CardTitle>
                        {rule.is_active && (
                          <span className="scoring-active-pill">
                            <Check size={12} /> Active
                          </span>
                        )}
                        {!rule.is_active && (
                          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
                            Inactive
                          </span>
                        )}
                      </div>
                      <CardDescription>{rule.description || 'No description provided'}</CardDescription>
                    </div>
                    <div className="assignment-priority-badge">
                      <Target size={12} />
                      Priority: {rule.priority}
                    </div>
                  </div>
                  <div className={`assignment-type-badge ${typeInfo.color} mt-3`}>
                    <Activity size={16} />
                    {typeInfo.label}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Conditions */}
                  {rule.conditions && rule.conditions.length > 0 && (
                    <div className="assignment-conditions-list mb-3">
                      <p className="text-xs font-semibold mb-2 text-muted-foreground">CONDITIONS:</p>
                      {rule.conditions.map((cond, idx) => (
                        <div key={idx} className="assignment-condition-item">
                          <ArrowRight size={14} className="text-primary" />
                          <span className="font-mono text-xs">
                            {cond.field} {cond.operator} <strong>"{cond.value}"</strong>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Assigned Users */}
                  {rule.assign_to_users && rule.assign_to_users.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold mb-2 text-muted-foreground">ASSIGNED TO:</p>
                      <div className="assignment-users-list">
                        {rule.assign_to_users.map((username) => (
                          <span key={username} className="assignment-user-chip">
                            <Users size={14} />
                            {username}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(rule)}
                    >
                      <Edit size={16} /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteRule(rule.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 size={16} /> Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Round Robin User Management */}
      <div className="workload-section" style={{ marginBottom: '2rem' }}>
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Users size={24} /> Round Robin User Pool
        </h2>
        <Card>
          <CardHeader>
            <CardTitle>Manage Round Robin Assignment Pool</CardTitle>
            <CardDescription>
              Add or remove users from the round robin assignment pool. Users with lower priority numbers get assigned first.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {users
                .filter(u => u.role === 'sales') // Only show visa consultants
                .sort((a, b) => (a.priority || 999) - (b.priority || 999)) // Sort by priority
                .map((user) => (
                  <div
                    key={user.username}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      background: user.is_active ? '#f9fafb' : '#fee',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: user.is_active ? '#A08EA8' : '#9ca3af',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '600'
                        }}
                      >
                        {user.username.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>
                          {user.consultant_name || user.username}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          @{user.username} • Priority: {user.priority || 1}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span
                        style={{
                          padding: '4px 8px',
                          background: user.is_active ? '#d1fae5' : '#fecaca',
                          color: user.is_active ? '#065f46' : '#991b1b',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}
                      >
                        {user.is_active ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </div>
                  </div>
                ))}
              {users.filter(u => u.role === 'sales').length === 0 && (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  No visa consultants found. Create sales users in User Management.
                </div>
              )}
            </div>
            <div style={{ marginTop: '1rem', padding: '12px', background: '#eff6ff', borderRadius: '6px' }}>
              <p style={{ fontSize: '13px', color: '#1e40af', margin: 0 }}>
                <strong>Note:</strong> To add/remove users or change priority, go to User Management page. 
                Only active visa consultants with role 'sales' are included in round robin assignment.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User Workload Section */}
      <div className="workload-section">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <BarChart2 size={24} /> Consultant Workload
        </h2>
        <div className="workload-grid">
          {workloads.map((workload) => (
            <Card key={workload.username} className="workload-card">
              <div className="workload-user-header">
                <div className="workload-avatar">
                  {workload.username.substring(0, 2).toUpperCase()}
                </div>
                <div>
                  <h3 className="font-semibold">{workload.username}</h3>
                  <p className="text-xs text-muted-foreground">
                    {workload.last_assigned ? `Last: ${new Date(workload.last_assigned).toLocaleDateString()}` : 'No assignments yet'}
                  </p>
                </div>
              </div>
              <div className="workload-stats">
                <div className="workload-stat">
                  <span className="text-2xl font-bold text-primary">{workload.active_leads}</span>
                  <span className="text-xs text-muted-foreground">Active Leads</span>
                </div>
                <div className="workload-stat">
                  <span className="text-2xl font-bold text-secondary">{workload.total_leads}</span>
                  <span className="text-xs text-muted-foreground">Total Leads</span>
                </div>
                <div className="workload-stat">
                  <span className="text-2xl font-bold text-accent">{workload.avg_lead_score.toFixed(1)}</span>
                  <span className="text-xs text-muted-foreground">Avg Score</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
        setIsEditDialogOpen(open);
        if (!open) {
          // Reset when closing
          setEditingRule(null);
          resetForm();
        }
      }}>
        <DialogContent className="assignment-dialog">
          <DialogHeader>
            <DialogTitle>Update Assignment Rule</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateRule}>
            {renderFormFields()}

            <div className="flex items-center gap-2 mt-4">
              <input
                type="checkbox"
                id="is-active-edit"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              <Label htmlFor="is-active-edit" className="cursor-pointer">
                <Zap size={16} className="inline" /> Active Rule
              </Label>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Update Rule</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssignmentRulesManager;
